import * as React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import Pagination from 'components/pagination/Pagination';

import SubcampaignsTableHeader from 'modules/subcampaigns/components/subcampaigns-table-header/SubcampaignsTableHeader';
import CcmCampaignOffersTable from '../table/CcmCampaignOffersTable';

import { __t, applyTranslations } from 'utils/language';
import { renderPrice } from 'utils/helper/price';
import { addThousandsSeparator } from 'utils/helper/common';
import {
  CCM_OFFER_TYPE_ACCEPTED,
  CCM_OFFER_TYPE_TO_STATE_MAP,
  hasSubmittedOutfits
} from '../utils/ccm-campaign-offers';

import './ccm-campaign-offers-list.scss';

applyTranslations(require('./ccm-campaign-offers-list.i18n.json'));

import type {
  CcmCampaignOverview,
  ContextMenuItem,
  LocationWindow,
  MessengerUnreadChatsMap,
  MetricsBarItem,
  ResponseTime,
  Subcampaign
} from 'types';
import type {
  CcmOfferResult,
  CcmOfferScreenState,
  CcmOfferTab
} from '../types';

type OwnProps = {
  campaignId: number,
  subcampaignId: number | null,
  ccmOverview: CcmCampaignOverview,
  activeTab: CcmOfferTab,
  poolScreenState: CcmOfferScreenState,
  unreadChatsMap: MessengerUnreadChatsMap,
  onPageChange: (page: number) => void,
  onRowClicked: (subcampaignId: number) => void,
  onRemoveFromRevoked: (subcampaign: Subcampaign | null | undefined) => void,
  onDownloadParticipants: () => void
};

type StateProps = {
  pool: Array<CcmOfferResult>,
  participants: Array<Subcampaign>,
  isLoadingParticipants: boolean,
  location: LocationWindow,
  responseTimeByInfluencer: {
    [x: number]: ResponseTime
  }
};

type Props = OwnProps & StateProps;

const MSG_KEY = 'ccm_campaign_offers_list';

const CcmCampaignOffersList: React.FC<Props> = ({
  campaignId,
  subcampaignId,
  ccmOverview,
  pool,
  participants,
  poolScreenState,
  isLoadingParticipants,
  location,
  activeTab,
  responseTimeByInfluencer,
  unreadChatsMap,
  onPageChange,
  onRowClicked,
  onRemoveFromRevoked,
  onDownloadParticipants
}): JSX.Element => {
  const getTableHeaderContextMenuItems = (): Array<ContextMenuItem> => {
    const hasOutfits = hasSubmittedOutfits(participants);

    if (!hasOutfits || activeTab !== CCM_OFFER_TYPE_ACCEPTED) {
      return [];
    }

    const downloadAsExcel: ContextMenuItem = {
      type: 'link',
      name: 'download-as-excel',
      text: __t(`${MSG_KEY}.context_menu.download_excel`),
      onClick: onDownloadParticipants
    };

    return [downloadAsExcel];
  };

  const getMetrics = (): Array<MetricsBarItem> => {
    if (isEmpty(ccmOverview)) {
      return [];
    }

    const currentState = CCM_OFFER_TYPE_TO_STATE_MAP[activeTab];

    const metrics: Array<MetricsBarItem> = [{
      key: 'creators',
      name: __t(`${MSG_KEY}.metrics.labels.creators`),
      value: ccmOverview[currentState.overviewName].creators || 0
    }];

    if (activeTab === CCM_OFFER_TYPE_ACCEPTED) {
      metrics.push(
        {
          key: 'published-outfits',
          name: __t(`${MSG_KEY}.metrics.labels.published_outfits`),
          value: ccmOverview[currentState.overviewName].publishedOutfits || 0
        },
        {
          key: 'total-affiliated-compensation',
          name: __t(`${MSG_KEY}.metrics.labels.total_affiliated_compensation`),
          value: renderPrice(ccmOverview[currentState.overviewName].totalAffiliatedCompensation || 0)
        },
        {
          key: 'total-outfit-clicks',
          name: __t(`${MSG_KEY}.metrics.labels.total_outfit_clicks`),
          value: addThousandsSeparator(ccmOverview[currentState.overviewName].totalOutfitClicks || 0)
        }
      );
    }

    return metrics;
  };

  const getData = (): Array<CcmOfferResult> => {
    if (activeTab === 'pool') {
      return pool ?? [];
    }

    return (participants || []).map(item => ({
      subcampaign: item,
      subcampaignId: item.id,
      offerState: item.offer.state,
      creatorProfile: item.creatorProfile,
      conversationExternalId: item.conversationExternalId,
      offer: item.offer
    }));
  };

  const renderData = (): React.ReactNode => {
    const showEmpty = (activeTab === 'pool' && !isNil(pool) && isEmpty(pool))
      || (activeTab !== 'pool' && !isLoadingParticipants && isEmpty(participants));

    if (showEmpty) {
      return (
        <div className="ccm-campaign-offers-list__empty">
          {__t(`${MSG_KEY}.empty_message.${activeTab}`)}
        </div>
      );
    }

    const showSpinner = (activeTab === 'pool' && isNil(pool))
      || (activeTab !== 'pool' && isLoadingParticipants);

    return (
      <React.Fragment>
        <div className="ccm-campaign-offers-list__header">
          <SubcampaignsTableHeader
            campaignId={campaignId}
            isEmpty={false}
            metrics={getMetrics()}
            participantsType={activeTab}
            contextMenuItems={getTableHeaderContextMenuItems()}
            showSpinner={showSpinner}
          />
        </div>
        <div className="ccm-campaign-offers-list__table">
          <CcmCampaignOffersTable
            data={getData()}
            campaignId={campaignId}
            subcampaignId={subcampaignId}
            activeTab={activeTab}
            responseTimeByInfluencer={responseTimeByInfluencer}
            unreadChatsMap={unreadChatsMap}
            showSpinner={showSpinner}
            onRowClicked={onRowClicked}
            onRemoveFromRevoked={onRemoveFromRevoked}
          />
        </div>
        {
          activeTab === 'pool' && (
            <div className="ccm-campaign-offers-list__pagination">
              <Pagination
                meta={poolScreenState.meta}
                links={poolScreenState.links}
                location={location}
                scrollToTopOnPageChange={false}
                onClick={(page: number) => onPageChange(page)}
              />
            </div>
          )
        }
      </React.Fragment>
    );
  };

  return (
    <div className="ccm-campaign-offers-list">
      {renderData()}
    </div>
  );
};

const mapStateToProps = ({
  campaignParticipants,
  ccmCampaign,
  influencerResponseTime,
  router
}): StateProps => ({
  pool: ccmCampaign.pool,
  participants: campaignParticipants.list,
  isLoadingParticipants: campaignParticipants.isLoading,
  location: router.location,
  responseTimeByInfluencer: influencerResponseTime.responseTimeData
});

export default connect(mapStateToProps)(CcmCampaignOffersList);
